import axios from 'axios'

interface HttpErrorResponse {
  status?: number
  statusCode?: number
  data?: any
  body?: string
}

/**
 * Library-agnostic check for HTTP errors from either axios or fetch error shapes.
 * Use instead of axios.isAxiosError() so callers don't depend on axios directly.
 */
export function isHttpError(error: unknown): error is Error & {
  statusCode?: number
  response?: HttpErrorResponse
} {
  if (axios.isAxiosError(error)) return true
  if (
    error instanceof Error &&
    typeof (error as any).statusCode === 'number' &&
    (error as any).response !== undefined
  ) {
    return true
  }
  return false
}

/**
 * Throws an error with response body and status attached for consistent handling
 * in catch blocks (e.response?.body, e.response?.statusCode, e.statusCode).
 * Use when a fetch Response is not ok instead of throwing a plain Error.
 */
export async function throwFetchError(
  response: Response,
  message?: string | ((body: string) => string)
): Promise<never> {
  const body = await response.text()
  let data: any = body
  try {
    data = JSON.parse(body)
  } catch {
    // keep as raw string when body is not valid JSON
  }
  // Callers that need the body in the message itself pass a builder, since the
  // stream is consumed here and cannot be read again.
  const resolvedMessage =
    typeof message === 'function' ? message(body) : message
  const err: any = new Error(
    resolvedMessage ?? `Request failed with status ${response.status}`
  )
  err.statusCode = response.status
  err.response = { statusCode: response.status, body, data }
  throw err
}

/**
 * Safely parse a fetch Response body.
 *
 * - JSON media types (application/json, application/problem+json, etc.) → JSON.parse
 * - text/* or missing/empty content-type → attempt JSON.parse, fall back to raw text
 * - any other explicit type (e.g. image/*, multipart/*) → raw text
 * - empty body → null
 */
export async function parseFetchResponse(response: Response): Promise<any> {
  const text = await response.text()
  if (!text) return null

  const contentType = (response.headers.get('content-type') ?? '').toLowerCase()

  const isJson = contentType.includes('/json') || contentType.includes('+json')
  if (isJson) {
    return JSON.parse(text)
  }

  const isTextual = !contentType || contentType.startsWith('text/')
  if (isTextual) {
    try {
      return JSON.parse(text)
    } catch {
      return text || null
    }
  }

  return text || null
}

export enum HttpLibrary {
  Fetch = 'fetch',
  Axios = 'axios',
}

export type SharedHttpRequest = {
  url: string
  options?: any
  library?: HttpLibrary
}

export interface NormalizedResponse {
  data: any
  status: number
  headers: Record<string, any>
}

/**
 * Normalizes the response from either fetch or axios to a consistent shape.
 * This allows calling code to work with responses regardless of which library was used.
 */
export function normalizeResponse(
  response: any,
  library: HttpLibrary
): NormalizedResponse {
  if (library === HttpLibrary.Axios) {
    // Handle empty responses (e.g., 204 No Content) - axios returns "" for empty body
    const data =
      response.data === '' || response.data === undefined ? null : response.data
    return {
      data,
      status: response.status,
      headers: response.headers,
    }
  }
  // Fetch response — body is already parsed, status/headers are top-level
  const data =
    response.data === '' || response.data === undefined ? null : response.data
  return {
    data,
    status: response.status,
    headers: response.headers,
  }
}

export const DefaultFetchHeaders: Record<string, string> = {
  Accept: 'application/json, text/plain, */*',
  'User-Agent': 'SageHomeLoans/1.0',
}

/**
 * SharedHttpClient provides a unified HTTP client interface that can use either
 * fetch or axios under the hood, controlled via feature flags.
 *
 * Key features:
 * - Consistent API regardless of underlying library
 * - Errors are normalized to a consistent shape across both libraries
 * - URL encoding is handled consistently to prevent double-encoding
 */
export class SharedHttpClient {
  static async post({
    url,
    options = {},
    library = HttpLibrary.Axios,
  }: SharedHttpRequest) {
    return this.makeRequest('post', url, options, library)
  }

  static async get({
    url,
    options = {},
    library = HttpLibrary.Axios,
  }: SharedHttpRequest) {
    return this.makeRequest('get', url, options, library)
  }

  static async patch({
    url,
    options = {},
    library = HttpLibrary.Axios,
  }: SharedHttpRequest) {
    return this.makeRequest('patch', url, options, library)
  }

  static async put({
    url,
    options = {},
    library = HttpLibrary.Axios,
  }: SharedHttpRequest) {
    return this.makeRequest('put', url, options, library)
  }

  static async delete({
    url,
    options = {},
    library = HttpLibrary.Axios,
  }: SharedHttpRequest) {
    return this.makeRequest('delete', url, options, library)
  }

  static async makeRequest(
    method: string,
    url: string,
    options: any,
    library: HttpLibrary
  ): Promise<any> {
    if (library === HttpLibrary.Fetch) {
      return this.makeFetchRequest(method, url, options)
    } else if (library === HttpLibrary.Axios) {
      return this.makeAxiosRequest(method, url, options)
    } else {
      throw new Error(`SharedHttpClient: Library ${library} not supported`)
    }
  }

  /**
   * Encode a value while preserving already-percent-encoded sequences
   * (e.g. Encompass paths like Public%5CCompanywide%5C).
   *
   * Note: app-level HTTP clients use a separate `encodeQueryValue` in httpUtils
   * that always encodes via encodeURIComponent (no pre-encoded passthrough).
   */
  private static safeEncodeValue(raw: string): string {
    try {
      const decoded = decodeURIComponent(raw)
      return decoded !== raw ? raw : encodeURIComponent(raw)
    } catch {
      return encodeURIComponent(raw)
    }
  }

  private static serializeQueryParams(params: Record<string, any>): string {
    const parts: string[] = []
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue

      if (Array.isArray(value)) {
        const arrayKey = `${encodeURIComponent(key)}[]`
        for (const item of value) {
          if (item !== undefined && item !== null) {
            parts.push(
              `${arrayKey}=${SharedHttpClient.safeEncodeValue(String(item))}`
            )
          }
        }
        continue
      }

      const encodedValue = SharedHttpClient.safeEncodeValue(String(value))
      parts.push(`${encodeURIComponent(key)}=${encodedValue}`)
    }
    return parts.join('&')
  }

  private static async makeFetchRequest(
    method: string,
    url: string,
    options: any
  ): Promise<any> {
    const fetchOptions = { ...options }

    // Resolve URL with query params (supports both 'params' and 'searchParams' keys)
    const queryParams = fetchOptions.searchParams ?? fetchOptions.params
    delete fetchOptions.searchParams
    delete fetchOptions.params

    let resolvedUrl = url
    if (queryParams && Object.keys(queryParams).length > 0) {
      resolvedUrl = `${url}?${this.serializeQueryParams(queryParams)}`
    }

    // Resolve body — supports both 'body' (raw) and 'json' (object to serialize)
    let body: BodyInit | undefined
    const headers: Record<string, string> = {
      ...DefaultFetchHeaders,
      ...fetchOptions.headers,
    }

    if (fetchOptions.json !== undefined) {
      body = JSON.stringify(fetchOptions.json)
      headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
    } else if (fetchOptions.body !== undefined) {
      const rawBody = fetchOptions.body
      const isPlainObject =
        typeof rawBody === 'object' &&
        rawBody !== null &&
        !(rawBody instanceof Blob) &&
        !(rawBody instanceof ArrayBuffer) &&
        !ArrayBuffer.isView(rawBody) &&
        !(rawBody instanceof FormData) &&
        !(rawBody instanceof URLSearchParams)
      if (isPlainObject) {
        body = JSON.stringify(rawBody)
        headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
      } else {
        body = rawBody as BodyInit
      }
    }

    delete fetchOptions.json
    delete fetchOptions.body
    delete fetchOptions.headers

    const response = await fetch(resolvedUrl, {
      method: method.toUpperCase(),
      headers,
      body,
      ...fetchOptions,
    })

    if (!response.ok) {
      const bodyText = await response.text()
      const error: any = new Error(
        `Request failed with status ${response.status}`
      )
      const responseHeaders: Record<string, string> = {}
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value
      })
      error.response = {
        statusCode: response.status,
        body: bodyText,
        headers: responseHeaders,
      }
      error.statusCode = response.status
      throw error
    }

    const data = await parseFetchResponse(response)

    const responseHeaders: Record<string, string> = {}
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value
    })

    return {
      data,
      status: response.status,
      headers: responseHeaders,
    }
  }

  private static async makeAxiosRequest(
    method: string,
    url: string,
    options: any
  ): Promise<any> {
    const axiosOptions = { ...options }

    // Support both 'body' (raw) and 'json' (object) request body conventions
    let requestBody = axiosOptions.body
    delete axiosOptions.body

    if (axiosOptions.json !== undefined) {
      requestBody = axiosOptions.json
      delete axiosOptions.json
    }

    // Map 'searchParams' to axios-style 'params'
    if (axiosOptions.searchParams && !axiosOptions.params) {
      axiosOptions.params = axiosOptions.searchParams
      delete axiosOptions.searchParams
    }

    // Add paramsSerializer to avoid double-encoding pre-escaped query params
    if (axiosOptions.params && !axiosOptions.paramsSerializer) {
      axiosOptions.paramsSerializer = {
        serialize: (params: Record<string, any>) =>
          SharedHttpClient.serializeQueryParams(params),
      }
    }

    // Execute axios request and normalize error shape for consistency with fetch errors
    try {
      switch (method) {
        case 'post':
          return await axios.post(url, requestBody, axiosOptions)
        case 'get':
          return await axios.get(url, axiosOptions)
        case 'patch':
          return await axios.patch(url, requestBody, axiosOptions)
        case 'put':
          return await axios.put(url, requestBody, axiosOptions)
        case 'delete':
          return await axios.delete(url, axiosOptions)
        default:
          throw new Error(`SharedHttpClient: Method ${method} not supported`)
      }
    } catch (axiosError: any) {
      if (axiosError.response) {
        const error: any = new Error(axiosError.message)
        error.response = {
          statusCode: axiosError.response.status,
          body:
            typeof axiosError.response.data === 'string'
              ? axiosError.response.data
              : JSON.stringify(axiosError.response.data),
          headers: axiosError.response.headers,
        }
        error.statusCode = axiosError.response.status
        throw error
      }
      throw axiosError
    }
  }
}

export default SharedHttpClient
